import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { Button, Card, Typography, Tag, Empty, Spin, Popconfirm, Input, Modal, DatePicker, InputNumber } from 'antd'
import { ArrowLeftOutlined, FallOutlined, PlusOutlined, RiseOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { useFeedback } from '@/utils/antdApp'
import AddStockDialog from '../components/add-stock-dialog'
import { colors } from '../../static'
import { getPoolDetail, addStockToPool, addStocksToPoolBatch, removeStockFromPool, exportPoolMissingFields, exportPoolFinancials } from '@/api'
import './index.scss'
import type { Stock } from '@/types'

const { Text } = Typography

function stockDisplayName(stock: Stock): string {
    if (stock.market === 'CN') return stock.sc_name || stock.name
    if (stock.market === 'HK') return stock.tc_name || stock.name
    return stock.name
}

/** 把接口返回的 CSV Blob 触发为浏览器下载 */
function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

export default function PoolDetailPage({
    poolId,
    idx,
    toDetail,
    toNewKB,
    onBack,
    onPoolChanged,
}: {
    poolId: number,
    idx: number,
    toDetail: (stock: Stock) => void
    toNewKB: (stock: Stock) => void
    onBack: () => void
    onPoolChanged?: () => void
}) {
    const { message } = useFeedback()
    const colorIndex = Number(idx ?? 0)
    const managerColor = colors[(Number.isFinite(colorIndex) ? colorIndex : 0) % colors.length]
    const [title, setTitle] = useState('')
    const [visible, setVisible] = useState(false)
    const [stocks, setStocks] = useState<Stock[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [exportOpen, setExportOpen] = useState(false)
    const [asOf, setAsOf] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [missingPeriods, setMissingPeriods] = useState<number>(16)
    const [exporting, setExporting] = useState(false)
    const [financialsOpen, setFinancialsOpen] = useState(false)
    const [financialsAsOf, setFinancialsAsOf] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [financialsPeriods, setFinancialsPeriods] = useState<number>(16)
    const [financialsExporting, setFinancialsExporting] = useState(false)

    const load = () => getPoolDetail(Number(poolId)).then(
        (data) => {
            setStocks(Array.isArray(data?.stocks) ? data.stocks : [])
            setTitle(data?.name || '')
        }
    ).catch(() => message.error('加载失败')).finally(() => setLoading(false))

    useEffect(() => {
        setStocks([])
        load()
    }, [poolId])

    const onAddStock = (data: Stock) => {
        addStockToPool({ pool_id: poolId, ...data }).then(() => {
            message.success('添加成功')
            load()
            onPoolChanged?.()
            setVisible(false)
        }).catch(() => message.error('添加失败'))
    }

    const onBatchAdd = async (batchStocks: { code: string; name: string }[]) => {
        try {
            const res = await addStocksToPoolBatch(poolId, batchStocks)
            const added = res.added?.length ?? 0
            const skipped = res.skipped?.length ?? 0
            message.success(`成功添加 ${added} 只${skipped ? `，跳过 ${skipped} 只` : ''}`)
            setVisible(false)
            load()
            onPoolChanged?.()
        } catch (err) {
            message.error(err instanceof Error ? err.message : '批量添加失败')
        }
    }

    const onRemoveStock = (code: string) => {
        removeStockFromPool(poolId, code).then(() => {
            message.success('已从股票池移除')
            load()
            onPoolChanged?.()
        }).catch((err: Error) => message.error(err.message || '删除失败'))
    }

    const onExportMissing = async () => {
        setExporting(true)
        try {
            const blob = await exportPoolMissingFields(Number(poolId), asOf, missingPeriods)
            downloadBlob(blob, `缺数据报告_${title || poolId}_${asOf}.csv`)
            message.success('导出成功')
            setExportOpen(false)
        } catch (err) {
            message.error(err instanceof Error ? err.message : '导出失败')
        } finally {
            setExporting(false)
        }
    }

    const onExportFinancials = async () => {
        setFinancialsExporting(true)
        try {
            const blob = await exportPoolFinancials(Number(poolId), financialsAsOf, financialsPeriods)
            downloadBlob(blob, `财报数据_${title || poolId}_${financialsAsOf}.csv`)
            message.success('导出成功')
            setFinancialsOpen(false)
        } catch (err) {
            message.error(err instanceof Error ? err.message : '导出失败')
        } finally {
            setFinancialsExporting(false)
        }
    }

    const query = search.trim().toLowerCase()
    const filteredStocks = query
        ? stocks.filter(s => [s.code, s.name, s.sc_name, s.tc_name]
            .some(v => (v || '').toLowerCase().includes(query)))
        : stocks

    return (
        <div className='pool-detail-page'>
            <div className='page-header flex flex-between pool-detail-page-header'>
                <div className="operate-manager-page__title-group">
                    <Button
                        type="text"
                        className="operate-pool-detail-page__back"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => onBack()}
                    >
                        全部股票池
                    </Button> / 
                    <div className="pool-detail-page__title color-1">{title}</div>
                </div>
                <div className='pool-detail-page-header-button'>
                    <Input
                        allowClear
                        placeholder="按代码或名称筛选"
                        prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 200, marginRight: 12 }}
                    />
                    <Text className='text-10' style={{ color: 'var(--text-3)' }}>
                        {query ? `${filteredStocks.length}/${stocks.length} 只` : `${stocks.length} 只股票`}
                    </Text>
                    <Button icon={<DownloadOutlined />} onClick={() => setExportOpen(true)} style={{ marginRight: 8 }}>
                        导出缺数据报告
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={() => setFinancialsOpen(true)} style={{ marginRight: 8 }}>
                        导出财报数据
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
                        添加个股
                    </Button>
                </div>
            </div>
            <div className='page-content'>
                {loading ? 
                    <div className='page-loading'>
                        <Spin />
                    </div> :
                    filteredStocks.length > 0 ? 
                    <div className='pool-detail-page-content-list'>
                        {filteredStocks.map((m) => (
                            <Card
                                key={m.id}
                                className="pool-detail-page-card"
                                style={{ '--manager-color': managerColor } as CSSProperties}
                                size="small"
                                onClick={() => toDetail(m)}
                            >
                                <div className='pool-detail-page-title'>
                                    <div className='pool-detail-page-title-top'>
                                        <div className='flex flex-between'>
                                            <Text style={{ fontSize: 14, fontWeight: 700 }}>{stockDisplayName(m)}</Text>
                                            <Tag variant='outlined' color='blue' className='text-10'>
                                                {m.market}
                                            </Tag>
                                        </div>
                                        <span className='pool-detail-page-title-tabs'>
                                            {m.code}
                                        </span>
                                    </div>
                                </div>
                                <div className='pool-detail-page-content'>
                                    <div className='pool-detail-page-middle'>
                                        <span className='pool-detail-page-middle-price'>￥{m.price ?? '--'}</span>
                                        <Tag
                                            key={m.change >= 0 ? 'up' : 'down'}
                                            color={m.change >= 0 ? 'error' : 'success'}
                                            icon={m.change >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                            variant="filled"
                                        >
                                            {m.change >= 0? '+': '-'}{m.change} %
                                        </Tag>
                                    </div>
                                    <div className='pool-detail-page-content-footer flex flex-between'>
                                        <span className='text-10' style={{ color: 'var(--text-3)' }}>{m.sector ?? '--'}</span>
                                        <div className='pool-detail-page-content-footer-button'>
                                            <Button
                                                size='small'
                                                style={{ marginRight: 10, color: 'var(--text-2)' }}
                                                className='text-11'
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    toDetail(m)
                                                }}>
                                                查看知识库
                                            </Button>
                                            <Button
                                                size='small'
                                                type='primary'
                                                className='text-11'
                                                style={{ marginRight: 10 }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    toNewKB(m)
                                                }}>
                                                新知识库
                                            </Button>
                                            <Popconfirm
                                                title="从股票池移除该个股？"
                                                okText="移除"
                                                cancelText="取消"
                                                onConfirm={() => onRemoveStock(m.code)}
                                            >
                                                <Button
                                                    size='small'
                                                    danger
                                                    className='text-11'
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    移除
                                                </Button>
                                            </Popconfirm>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                    : <div className='pool-detail-page-content-list-empty'>
                        <Empty description={query ? '未找到匹配的股票' : '暂无股票'} />
                    </div>
                }
            </div>
            <AddStockDialog 
                open={visible} 
                onCancel={() => setVisible(false)} 
                onAdd={onAddStock}
                onBatchAdd={onBatchAdd}
                poolId={Number(poolId)} 
                items={stocks} 
            />
            <Modal
                open={exportOpen}
                title="导出缺数据报告"
                okText="导出"
                cancelText="取消"
                confirmLoading={exporting}
                onOk={onExportMissing}
                onCancel={() => setExportOpen(false)}
            >
                <div style={{ marginBottom: 8, color: 'var(--text-3)' }}>
                    选择决策数据截止日（as_of），导出该时点每只美股往前若干期里每一期缺失的底层财报字段（按时间倒序，CSV）。
                </div>
                <DatePicker
                    value={dayjs(asOf)}
                    style={{ width: '100%', marginBottom: 12 }}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    onChange={(d: Dayjs | null) => { if (d) setAsOf(d.format('YYYY-MM-DD')) }}
                />
                <div style={{ marginBottom: 4, color: 'var(--text-3)' }}>每只个股导出的最近期数</div>
                <InputNumber
                    min={1}
                    max={64}
                    style={{ width: '100%' }}
                    value={missingPeriods}
                    onChange={(v) => setMissingPeriods(Number(v) || 16)}
                />
            </Modal>
            <Modal
                open={financialsOpen}
                title="导出财报数据"
                okText="导出"
                cancelText="取消"
                confirmLoading={financialsExporting}
                onOk={onExportFinancials}
                onCancel={() => setFinancialsOpen(false)}
            >
                <div style={{ marginBottom: 8, color: 'var(--text-3)' }}>
                    导出池内全部个股（美股 + 港/A 股）的历史财报宽表：一行一期，表头为股票代码、
                    报告会计截止日期、报告类型、财报发布日期，其后每个有值字段各占一列，便于逐期对比数值（CSV）。
                    列名为 ops 接口原始字段名（比率以小数返回，如 0.4621 = 46.21%），与决策口径的字段名不同。
                </div>
                <DatePicker
                    value={dayjs(financialsAsOf)}
                    style={{ width: '100%', marginBottom: 12 }}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    onChange={(d: Dayjs | null) => { if (d) setFinancialsAsOf(d.format('YYYY-MM-DD')) }}
                />
                <div style={{ marginBottom: 4, color: 'var(--text-3)' }}>每只个股导出的最近期数</div>
                <InputNumber
                    min={1}
                    max={64}
                    style={{ width: '100%' }}
                    value={financialsPeriods}
                    onChange={(v) => setFinancialsPeriods(Number(v) || 16)}
                />
            </Modal>
        </div>
    )
}
